import MathInput from "react-math-keyboard";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import type { MathField } from "react-math-keyboard/dist/cjs/types/types/types";
import type { NullOrCallback } from "@/helpers/types.ts";

interface LatexKeyboardProps {
  openKeyboardFunc: (arg0: NullOrCallback) => void;
  onFocus?: (focused: boolean) => void;
  onNewInput: (text: string) => void;
}

export const LatexKeyboard: FC<LatexKeyboardProps> = ({
  openKeyboardFunc,
  onNewInput,
  onFocus,
}) => {
  const mathFieldRef = useRef<MathField>(null);
  const clearRef = useRef<() => void | null>(null);
  const [value, setValue] = useState<string>("");

  const openKeyboard = useCallback(() => {
    if (mathFieldRef.current) mathFieldRef.current.focus();
    else console.warn("Cannot open keyboard, mathFieldRef is null (called too soon?)");
  }, []);

  useEffect(() => {
    const mathField = mathFieldRef.current;

    if (!mathField || !onFocus) return;

    const element = mathField.el();

    const focusIn = () => onFocus(true);
    const focusOut = () => onFocus(false);

    element.addEventListener("focusout", focusOut);
    element.addEventListener("focusin", focusIn);

    return () => {
      element.removeEventListener("focusout", focusOut);
      element.removeEventListener("focusin", focusIn);
    };
  }, [onFocus]);

  useEffect(() => {
    if (value === "") return;

    onNewInput(value);

    if (clearRef.current) clearRef.current();
  }, [onNewInput, value]);

  useEffect(() => {
    openKeyboardFunc(openKeyboard);

    return () => {
      openKeyboardFunc(null);
    };
  }, [openKeyboardFunc, openKeyboard]);

  useEffect(() => {}, []);

  return (
    <MathInput
      scrollType={"window"}
      // Note 1: Style is needed as this is passed indirectly to another component
      // Styled components would not work here
      // Note 2: 0 width and 0 height is required to hide the latex input
      // Using visibility/block would cause .focus() to fail
      style={{ width: 0, height: 0 }}
      setMathfieldRef={(math) => {
        mathFieldRef.current = math;
      }}
      setClearRef={(callback) => {
        clearRef.current = callback;
      }}
      // Note: Do not call onNewInput directly within setValue
      // This causes react compiler to 'shit itself'
      // to the point of "use no memo" does not help :)
      setValue={(text) => {
        setValue(text);
      }}
      scrollTriesToShowLastElement={true}
      isPaddingPersistent={true}
    />
  );
};
